import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeam } from '../../context/TeamContext';
import apiClient from '../../lib/api';
import {
  Users,
  Settings,
  UserPlus,
  Trash2,
  Crown,
  Shield,
  User,
  Eye,
  ChevronDown,
  Loader2,
  AlertCircle,
  Check,
  Database,
} from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', icon: Shield, description: 'Can manage team and members' },
  { value: 'member', label: 'Member', icon: User, description: 'Can view and edit data' },
  { value: 'viewer', label: 'Viewer', icon: Eye, description: 'Read-only access' },
];

const TeamSettings = () => {
  const navigate = useNavigate();
  const {
    currentTeam,
    teamRole,
    canManageTeam,
    isOwner,
    updateTeam,
    deleteTeam,
    loading: teamLoading,
  } = useTeam();

  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Team settings form
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');

  // Add member form
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('member');
  const [addingMember, setAddingMember] = useState(false);

  // Role change dropdown
  const [editingMemberId, setEditingMemberId] = useState(null);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // All Teams admin section
  const [allTeams, setAllTeams] = useState([]);
  const [allTeamsLoading, setAllTeamsLoading] = useState(false);
  const [isAppAdmin, setIsAppAdmin] = useState(false);
  const [deletingTeamId, setDeletingTeamId] = useState(null);
  const [confirmDeleteTeamId, setConfirmDeleteTeamId] = useState(null);

  // Load team members and all users
  useEffect(() => {
    const loadData = async () => {
      if (!currentTeam) return;

      try {
        setLoading(true);
        setError(null);

        const [membersData, usersData] = await Promise.all([
          apiClient.getTeamMembers(currentTeam.id),
          apiClient.getAllUsers().catch(() => []),
        ]);

        setMembers(membersData);
        setAllUsers(usersData);
        setTeamName(currentTeam.name);
        setTeamDescription(currentTeam.description || '');
      } catch (err) {
        console.error('Error loading team data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentTeam]);

  // Load all teams for admin section
  useEffect(() => {
    const loadAllTeams = async () => {
      try {
        setAllTeamsLoading(true);
        const teamsData = await apiClient.getAllTeamsAdmin();
        setAllTeams(teamsData);
        setIsAppAdmin(true);
      } catch (err) {
        // User is not an app admin - that's fine, just hide the section
        setIsAppAdmin(false);
      } finally {
        setAllTeamsLoading(false);
      }
    };

    loadAllTeams();
  }, []);

  const handleAdminDeleteTeam = async (teamId) => {
    try {
      setDeletingTeamId(teamId);
      await apiClient.adminDeleteTeam(teamId);
      setAllTeams(prev => prev.filter(t => t.id !== teamId));
      setConfirmDeleteTeamId(null);
    } catch (err) {
      console.error('Error deleting team:', err);
      setError(err.message);
    } finally {
      setDeletingTeamId(null);
    }
  };

  // Get users not in team (for add member dropdown)
  const availableUsers = allUsers.filter(
    user => !members.some(m => m.userId === user.id)
  );

  const handleSaveSettings = async () => {
    if (!teamName.trim()) return;

    try {
      setSaving(true);
      await updateTeam({
        name: teamName.trim(),
        description: teamDescription.trim() || null,
      });
    } catch (err) {
      console.error('Error saving team settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) return;

    try {
      setAddingMember(true);
      const newMember = await apiClient.addTeamMember(currentTeam.id, selectedUserId, selectedRole);
      setMembers(prev => [...prev, newMember]);
      setShowAddMember(false);
      setSelectedUserId('');
      setSelectedRole('member');
    } catch (err) {
      console.error('Error adding member:', err);
      setError(err.message);
    } finally {
      setAddingMember(false);
    }
  };

  const handleUpdateMemberRole = async (memberId, newRole) => {
    try {
      await apiClient.updateTeamMember(currentTeam.id, memberId, newRole);
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: newRole } : m));
      setEditingMemberId(null);
    } catch (err) {
      console.error('Error updating member role:', err);
      setError(err.message);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await apiClient.removeTeamMember(currentTeam.id, memberId);
      setMembers(prev => prev.filter(m => m.id !== memberId));
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.message);
    }
  };

  const handleDeleteTeam = async () => {
    try {
      setDeleting(true);
      await deleteTeam();
      navigate('/');
    } catch (err) {
      console.error('Error deleting team:', err);
      setError(err.message);
      setDeleting(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return Crown;
      case 'admin': return Shield;
      case 'member': return User;
      case 'viewer': return Eye;
      default: return User;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-700';
      case 'admin': return 'bg-blue-100 text-blue-700';
      case 'member': return 'bg-green-100 text-green-700';
      case 'viewer': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (teamLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No team selected</p>
      </div>
    );
  }

  if (!canManageTeam) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-yellow-800">Access Restricted</h3>
            <p className="text-sm text-yellow-700 mt-1">
              You don't have permission to manage team settings. Contact a team admin for assistance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Team Settings</h1>
        <p className="text-gray-600 mt-1">Manage your team and members</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-sm text-red-600 hover:text-red-800 mt-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Team Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Settings className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Team Information</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Team Name
            </label>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter team name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={teamDescription}
              onChange={(e) => setTeamDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Describe your team"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={saving || !teamName.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
              <p className="text-sm text-gray-500">{members.length} members</p>
            </div>
          </div>

          <button
            onClick={() => setShowAddMember(!showAddMember)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Add Member
          </button>
        </div>

        {/* Add Member Form */}
        {showAddMember && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="font-medium text-gray-900 mb-4">Add New Member</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select User
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Choose a user...</option>
                  {availableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowAddMember(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                disabled={!selectedUserId || addingMember}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {addingMember ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                Add Member
              </button>
            </div>
          </div>
        )}

        {/* Members List */}
        <div className="space-y-2">
          {members.map((member) => {
            const RoleIcon = getRoleIcon(member.role);
            const canModify = isOwner || (teamRole === 'admin' && member.role !== 'admin' && member.role !== 'owner');

            return (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                    {(member.userName || member.userEmail || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {member.userName || member.userEmail || 'Unknown User'}
                    </p>
                    <p className="text-sm text-gray-500">{member.userEmail}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {editingMemberId === member.id && canModify && member.role !== 'owner' ? (
                    <select
                      value={member.role}
                      onChange={(e) => handleUpdateMemberRole(member.id, e.target.value)}
                      onBlur={() => setEditingMemberId(null)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                    >
                      {ROLE_OPTIONS.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <button
                      onClick={() => canModify && member.role !== 'owner' && setEditingMemberId(member.id)}
                      disabled={!canModify || member.role === 'owner'}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${getRoleBadgeColor(member.role)} ${
                        canModify && member.role !== 'owner' ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
                      }`}
                    >
                      <RoleIcon className="w-3.5 h-3.5" />
                      <span className="capitalize">{member.role}</span>
                      {canModify && member.role !== 'owner' && (
                        <ChevronDown className="w-3 h-3" />
                      )}
                    </button>
                  )}

                  {canModify && member.role !== 'owner' && (
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove member"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Danger Zone */}
      {isOwner && (
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-red-900">Danger Zone</h2>
              <p className="text-sm text-red-600">Irreversible actions</p>
            </div>
          </div>

          {showDeleteConfirm ? (
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-red-800 mb-4">
                Are you sure you want to delete <strong>{currentTeam.name}</strong>?
                This will permanently delete all team data and cannot be undone.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTeam}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Team
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete Team
            </button>
          )}
        </div>
      )}

      {/* All Teams Admin Section */}
      {isAppAdmin && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">All Teams (Admin)</h2>
                <p className="text-sm text-gray-500">
                  {allTeams.length} teams total, {allTeams.filter(t => t.itemCount === 0).length} empty
                </p>
              </div>
            </div>
          </div>

          {allTeamsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-2">
              {allTeams.map((team) => (
                <div
                  key={team.id}
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    team.itemCount === 0 ? 'bg-red-50 border border-red-100' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 truncate">{team.name}</p>
                      {team.slug === 'default-team' && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                          Default
                        </span>
                      )}
                      {team.itemCount === 0 && team.slug !== 'default-team' && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
                          Empty
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {team.memberCount} members &middot; {team.itemCount} items
                    </p>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    {team.slug !== 'default-team' && (
                      <>
                        {confirmDeleteTeamId === team.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-red-600">Delete?</span>
                            <button
                              onClick={() => setConfirmDeleteTeamId(null)}
                              className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                              No
                            </button>
                            <button
                              onClick={() => handleAdminDeleteTeam(team.id)}
                              disabled={deletingTeamId === team.id}
                              className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                            >
                              {deletingTeamId === team.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Trash2 className="w-3 h-3" />
                              )}
                              Yes
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteTeamId(team.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              team.itemCount === 0
                                ? 'text-red-500 hover:text-red-700 hover:bg-red-100'
                                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                            }`}
                            title={team.itemCount > 0 ? `Team has ${team.itemCount} items` : 'Delete empty team'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamSettings;
